import {
  faArrowRotateBack,
  faCheck,
  faRotateBack,
  faSave,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon, FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { Chip } from '@material-ui/core';
import React, { Dispatch, ReactElement, SetStateAction, useEffect, useState } from 'react';
import VoxelButton from '../../../../../../components/voxel/Buttons/VoxelButton';
import VoxelIconButton from '../../../../../../components/voxel/Buttons/VoxelIconButton';
import VoxelText from '../../../../../../components/voxel/Text/VoxelText';
import VoxelTooltipShort from '../../../../../../components/voxel/Tooltip/VoxelTooltipShort';
import { ContentTaskGq } from '../../../../../../graphql/projects/models/ContentTaskGq';
import { UpdateTaskDetailsCommand } from '../../../../../../graphql/projects/mutations/contentPlan/useUpdateTaskDetailsGq';
import { projectsApiService } from '../../../../../../graphql/projects/projectsApiService';

export interface ContentTaskDetailInputProps {
  reload: () => Promise<unknown>;
  contentTask: ContentTaskGq;
  label: string;
  valueKey: keyof UpdateTaskDetailsCommand;
  originalValue?: string;
  newValue?: string;
  ruleValue?: string;
  setUpdating: Dispatch<SetStateAction<boolean>>;
  updating: boolean;
  maxLength?: number;
  showCount?: boolean;
  areaSize?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showManual?: boolean;
  showGenerated?: boolean;
  icon?: ReactElement<FontAwesomeIconProps>;
}

export const ContentTaskDetailInput: React.FC<ContentTaskDetailInputProps> = ({
  reload,
  contentTask,
  label,
  valueKey,
  updating,
  setUpdating,
  originalValue,
  newValue,
  maxLength,
  showCount,
  ruleValue,
  areaSize = 'sm',
  showManual,
  showGenerated,
  icon,
}) => {
  // Form logic
  const initialValue = newValue ?? ruleValue ?? originalValue ?? '';
  const [value, setValue] = useState<string>(initialValue);
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const handleCancel = () => setValue(newValue ?? originalValue ?? '');

  const mutation = projectsApiService.mutations.contentPlan.useUpdateTaskDetails();
  const handleSubmit = () => {
    setUpdating(true);
    mutation({ taskId: contentTask.id, [valueKey]: value, projectId: contentTask.projectId })
      .then(reload)
      .finally(() => setUpdating(false));
  };

  const automaticValue = ruleValue ?? originalValue;
  const ruleCreated = !!ruleValue?.length && ruleValue === value;
  const manuallyCreated = !!value?.length && value !== automaticValue;

  return (
    <div
      className={`flex flex-col items-stretch gap-1
    ${areaSize === 'sm' ? 'h-[32px] grow-0' : ''}
    ${areaSize === 'md' ? 'h-[75px] grow-0' : ''}
    ${areaSize === 'lg' ? 'h-[100px] grow-0' : ''}
    ${areaSize === 'xl' ? 'h-[200px] grow-0' : ''}
    ${areaSize === 'full' ? 'h-full grow' : ''}
    `}
    >
      <div className="grow-0 flex flex-row items-center flex-nowrap">
        {icon && <div className="mx-1">{icon}</div>}
        <VoxelText div variant={'title3'}>
          {label}
        </VoxelText>
        {manuallyCreated && (
          <div className="bg-voxel-gray-200 rounded-full font-sans text-xs font-medium px-2.5 py-0.5 ml-2">
            Manual
          </div>
        )}
        {ruleCreated && (
          <VoxelTooltipShort message={'Original: ' + (originalValue?.length ? originalValue : ' - ')}>
            <Chip label="Generated" size="small" />
          </VoxelTooltipShort>
        )}
      </div>
      <div className="grow">
        <form className="h-full">
          <textarea
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={updating}
            placeholder={label + '...'}
            readOnly={updating}
            className={`
          bg-voxel-gray-50 rounded-md w-full p-3 resize-none h-full
          focus:outline-1 focus:outline-voxel-gray-300 
            `}
          />
        </form>
      </div>
      <div className="grow-0 mt-0.5 ml-1 flex flex-row justify-between">
        <VoxelText
          div
          variant="form-caption"
          color="medium"
          colorClassName={value?.length > maxLength ? 'text-red-600' : 'medium'}
        >
          {value?.length} {!!maxLength ? '/' + maxLength : ''} characters
        </VoxelText>
        <div className="flex flex-row items-center pl-1 flex-nowrap">
          <VoxelIconButton
            type={'submit'}
            disabled={updating || value === initialValue}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              handleSubmit();
            }}
            size={'sm'}
            color="white"
            icon={<FontAwesomeIcon icon={faSave} size="lg" />}
          />
          <VoxelIconButton
            disabled={updating || value === initialValue}
            onClick={() => handleCancel()}
            size={'sm'}
            color="white"
            icon={<FontAwesomeIcon icon={faArrowRotateBack} size="lg" />}
          />
        </div>
      </div>
    </div>
  );
};

export default ContentTaskDetailInput;
